import type { ReactNode } from 'react'

const P = 'mt-4 text-body text-charcoal first:mt-0'

interface ReasonProps {
  index: string
  title: string
  children: ReactNode
}

/** One reason per card: title column on the left, body copy at reading width on the right. */
function Reason({ index, title, children }: ReasonProps) {
  return (
    <li className="grid grid-cols-1 gap-5 rounded-3xl border border-smoke bg-white p-6 tablet:grid-cols-12 tablet:gap-10 tablet:p-10">
      <div className="tablet:col-span-4">
        <span aria-hidden="true" className="block text-body-sm-bold text-green-dark tabular-nums">
          {index}
        </span>
        <h3 className="text-title-xs mt-2 text-charcoal">{title}</h3>
      </div>
      <div className="max-w-2xl tablet:col-span-8">{children}</div>
    </li>
  )
}

/** Compliance-approved article copy — keep verbatim. */
export function HighEarnersArticle() {
  return (
    <article aria-labelledby="ontrack-article-heading" className="mx-auto max-w-300 px-4 tablet:px-8">
      <h2 id="ontrack-article-heading" className="text-title-lg text-charcoal">
        What high earners need to know about retirement planning
      </h2>
      <p className="mt-4 text-body text-neutral-darker tablet:mt-5 tablet:text-body-lg">
        Retirement calculations get more complex the more money you make — and planning gets more complicated. Here are
        three reasons why:
      </p>

      <ol className="mt-8 flex flex-col gap-4 tablet:mt-10 tablet:gap-6">
        <Reason index="01" title="Retirement account limits can be too low">
          <p className={P}>
            When you earn $200,000 a year, maxing out your 401(k) and an IRA puts you above the commonly recommended
            savings rate of 15%, before even considering employer contributions. Many workers who earn less than that
            spend years working their way toward maxing out.
          </p>
          <p className={P}>
            But as your income grows, the limits those more common retirement accounts impose can actually become
            restrictive and start to stifle your savings rate. Add that to the fact that many financial advisors
            recommend an even higher rate for high earners — up to 20% or 25% of income per year — and you may find you
            need to turn to other accounts or more advanced strategies. This is when careful tax planning and
            optimization can come into play.
          </p>
          <p className={P}>
            Many people turn to taxable brokerage accounts when they&apos;ve maxed out a 401(k) and IRA. These
            don&apos;t offer the tax advantages of designated retirement accounts, but they can introduce some tax
            diversification in retirement — and have fewer rules on when and how you can withdraw the money, which is
            especially important for those who want to achieve financial independence a little (or a lot) early.
          </p>
          <p className={P}>
            If you own your own business or even do consulting on the side, you may be able to explore other
            tax-advantaged retirement accounts that could potentially let you stash away more than the typical 401(k)
            and IRA combo — examples include a SEP IRA or solo 401(k). There are also the backdoor and mega‑backdoor
            strategies, which involve moving funds from a traditional IRA or after-tax 401(k) into a Roth IRA, where
            investment growth may be tax-free and distributions typically aren&apos;t taxed as income in retirement. And
            if you have a high-deductible health plan, the HSA and its triple tax benefit — tax-free contributions,
            tax-free growth and tax-free eligible withdrawals — can be valuable despite low contribution limits.
          </p>
        </Reason>

        <Reason index="02" title="Tax optimization matters">
          <p className={P}>
            Whenever you have a mix of different accounts with different tax treatments, you want to dial in on asset
            location strategies — in other words, putting the right investments in the right accounts to maximize tax
            efficiency.
          </p>
          <p className={P}>
            Examples of what that can look like: investment earnings in a taxable brokerage account can generate capital
            gains, which are subject to either long-term capital gains taxes (more favorable) or ordinary income tax
            rates, depending on how long you&apos;ve held the investment. This generally means you may want to put
            tax-efficient investments in these accounts — things like municipal bond funds, index funds or
            exchange-traded funds, or tax-efficiency-focused mutual funds, as examples.
          </p>
          <p className={P}>
            On the other side, you might use your tax-advantaged accounts to hold investments that are likely to trigger
            a tax burden. These are often investments that generate income, like high-yield bonds or dividend stocks,
            real estate investment trusts or actively managed mutual funds.
          </p>
          <p className={P}>
            If you&apos;re not sure, this sort of guidance should be part of any financial advisor&apos;s strategy. And
            when you&apos;ve entered this stage of complexity with your finances, working with an advisor can be
            beneficial.
          </p>
        </Reason>

        <Reason index="03" title="Social Security becomes less meaningful">
          <p className={P}>
            Your Social Security benefit in retirement is based on &quot;average indexed monthly earnings,&quot; which
            essentially translates your annual earnings into today&apos;s wages, and then takes the average of the
            highest 35 years. There&apos;s also a maximum taxable income each year, and only income up to that threshold
            is counted in this calculation.
          </p>
          <p className={P}>
            On top of that, the program is designed to help lower earners fund their retirement, and so the benefit
            formula is progressive, meaning the percentage of your salary that Social Security can replace gets smaller
            at higher incomes. For maximum earners, the latest figures say it will replace about 28% of benefits.
          </p>
          <p className={P}>
            That doesn&apos;t mean you should ignore Social Security benefits in your calculations — there&apos;s a
            reason we included other monthly income in the calculator. Even relatively small amounts can change your
            projections and the amount of savings you need to accumulate. But keep in mind that Social Security is
            likely going to be a small part of your strategy.
          </p>
        </Reason>
      </ol>
    </article>
  )
}
